
import { defineComponent, onMounted, computed, ref } from "vue";
import { useRoute } from "vue-router";
import { Modal } from "bootstrap";

import ChargebacksActionToolbar from "@/layout/toolbar/ChargebacksActionToolbar.vue";
import { setCurrentPageTitle } from "@/core/helpers/breadcrumb";
import Details from "@/components/merchants/chargebacks/view/Details.vue";
import Summary from "@/components/merchants/chargebacks/incoming/Summary.vue";
import ApiService from "@/core/services/ApiService";
import { DrawerComponent } from "@/assets/ts/components/_DrawerComponent";
import AddChargebackResponseDrawer from "@/layout/extras/AddChargebackResponseDrawer.vue";
import ViewIncomingDetailsDrawer from "@/layout/extras/ViewIncomingDetailsDrawer.vue";
import SearchApiModal from "@/components/modals/general/SearchApiModal.vue";
import PdfViewModal from "@/components/modals/general/PdfViewModal.vue";
import QRViewModal from "@/components/modals/general/QRViewModal.vue";

interface Incoming {
  id: string;
  transaction_found: string;
  details_populated: string;
  dispute_url: string;
  is_responded: string;
  // eslint-disable-next-line
  card_holder: any;
  // eslint-disable-next-line
  card: any;
  // eslint-disable-next-line
  transaction: any;
  // eslint-disable-next-line
  order_details: any;
  // eslint-disable-next-line
  product_details: any;
  merchant_id: string;
  reason: string;
  // eslint-disable-next-line
  dispute_cycle: any;
  connection_id: string;
  found_date: string;
  populated_date: string;
  sent_date: string;
  reason_condition: string;
}

interface Merchant {
  dba_name: string;
  processor_id: string;
  // eslint-disable-next-line
  merchantDetails: any;
}

interface Connection {
  id: string;
  conn_name: string;
  scheme: string;
  host: string;
  domain: string;
  auth_type: string;
  payload_type: string;
  response_type: string;
  enabled: string;
  conn_status: string;
  category: string;
  created: string;
  modified: string;
  logo_url: string;
}

export default defineComponent({
  name: "incoming-details",
  components: {
    Details,
    Summary,
    ChargebacksActionToolbar,
    AddChargebackResponseDrawer,
    ViewIncomingDetailsDrawer,
    SearchApiModal,
    PdfViewModal,
    QRViewModal,
  },
  setup() {
    const route = useRoute();
    const getIncomingId = computed(() => {
      return route.params.id;
    });
    const incoming = ref<Incoming>({
      id: "",
      transaction_found: "0",
      details_populated: "0",
      dispute_url: "",
      is_responded: "",
      card_holder: {},
      card: {},
      transaction: {
        action: {},
      },
      order_details: {
        items: [],
      },
      product_details: {},
      merchant_id: "",
      reason: "",
      dispute_cycle: {},
      connection_id: "",
      found_date: "0000-00-00 00:00:00",
      populated_date: "0000-00-00 00:00:00",
      sent_date: "0000-00-00 00:00:00",
      reason_condition: "",
    });
    const merchant = ref<Merchant>({
      dba_name: "",
      processor_id: "",
      merchantDetails: {},
    });
    const reason = ref({});
    const reason_explained = ref({
      message: {},
      instructions: {},
    });
    const processor = ref({});
    const templates = ref({});
    const templateImages = ref([]);

    onMounted(async () => {
      setCurrentPageTitle("Incoming Chargeback Details");

      setTimeout(async () => {
        await getIncoming();
        await getConnections();
        DrawerComponent.reinitialization();
      }, 500);
    });

    const connections = ref<Connection[]>([]);
    const getConnections = async () => {
      const { data } = await ApiService.get("apis/connections?limit=100");
      if (data.success) {
        connections.value = [...data.data];
      }
    };

    const getFoundTime = computed(() => {
      const time = incoming.value.found_date.split(" ")[1];
      const partTime = time.split(":");
      return `${partTime[0]}:${partTime[1]}`;
    });
    const getFoundDate = computed(() => {
      const date = incoming.value.found_date.split(" ")[0];
      const partDate = date.split("-");
      return `${partDate[1]}-${partDate[2]}`;
    });
    const getPopulatedTime = computed(() => {
      const time = incoming.value.populated_date.split(" ")[1];
      const partTime = time.split(":");
      return `${partTime[0]}:${partTime[1]}`;
    });
    const getPopulatedDate = computed(() => {
      const date = incoming.value.populated_date.split(" ")[0];
      const partDate = date.split("-");
      return `${partDate[1]}-${partDate[2]}`;
    });
    const getSentTime = computed(() => {
      const time = incoming.value.sent_date.split(" ")[1];
      const partTime = time.split(":");
      return `${partTime[0]}:${partTime[1]}`;
    });
    const getSentDate = computed(() => {
      const date = incoming.value.sent_date.split(" ")[0];
      const partDate = date.split("-");
      return `${partDate[1]}-${partDate[2]}`;
    });
    const apiSyncItems = ref([]);

    const getIncoming = async () => {
      try {
        const { data } = await ApiService.get(
          `chargebacks/incoming?id=${getIncomingId.value}`
        );
        incoming.value = data.data[0];
        try {
          incoming.value.card_holder = JSON.parse(incoming.value.card_holder);
        } catch (e) {
          incoming.value.card_holder = {};
        }

        try {
          incoming.value.card = JSON.parse(incoming.value.card);
        } catch (e) {
          incoming.value.card = {};
        }

        try {
          incoming.value.transaction = JSON.parse(incoming.value.transaction);
        } catch (e) {
          incoming.value.transaction = { action: {} };
        }

        try {
          incoming.value.dispute_cycle = JSON.parse(
            incoming.value.dispute_cycle
          );
        } catch (e) {
          incoming.value.dispute_cycle = {};
        }

        try {
          incoming.value.order_details = JSON.parse(
            incoming.value.order_details
          );
        } catch (e) {
          incoming.value.order_details = { items: [] };
        }

        try {
          incoming.value.product_details = JSON.parse(
            incoming.value.product_details
          );
        } catch (e) {
          incoming.value.product_details = {};
        }
      } catch (err) {
        incoming.value = {
          id: "",
          transaction_found: "0",
          details_populated: "0",
          dispute_url: "",
          is_responded: "",
          card_holder: {},
          card: {},
          transaction: {
            action: {},
          },
          order_details: {
            items: [],
          },
          product_details: {},
          merchant_id: "",
          reason: "",
          dispute_cycle: {},
          connection_id: "",
          found_date: "0000-00-00 00:00:00",
          populated_date: "0000-00-00 00:00:00",
          sent_date: "0000-00-00 00:00:00",
          reason_condition: "",
        };
      }

      try {
        const { data } = await ApiService.get(
          `merchants/accounts?merchant_id=${incoming.value.merchant_id}`
        );
        merchant.value = data.data[0];
        try {
          merchant.value.merchantDetails = JSON.parse(
            merchant.value.merchantDetails
          );
        } catch (e) {
          merchant.value.merchantDetails = {};
        }
      } catch (err) {
        merchant.value = {
          dba_name: "",
          processor_id: "",
          merchantDetails: {},
        };
      }

      try {
        const { data } = await ApiService.get(
          `chargebacks/reasons?code=${incoming.value.reason}`
        );
        reason.value = data.data[0];
      } catch (err) {
        reason.value = {};
      }

      try {
        const { data } = await ApiService.get(
          `chargebacks/reasons_explained?code=${incoming.value.reason}`
        );
        reason_explained.value = data.data[0];
        reason_explained.value.message = JSON.parse(data.data[0].message);
        reason_explained.value.instructions = JSON.parse(
          data.data[0].instructions
        );
      } catch (err) {
        reason_explained.value = { message: {}, instructions: {} };
      }

      try {
        const { data } = await ApiService.get(
          `processors/isos/${merchant.value.processor_id}`
        );
        processor.value = data.data[0];
      } catch (err) {
        processor.value = {};
      }

      try {
        const { data } = await ApiService.get(
          `apis/merchants?fields=id,alias_name,connection_id,endpoint_name&category=Gateway&merchant_id=${incoming.value.merchant_id}`
        );
        apiSyncItems.value = data.data;
      } catch (e) {
        apiSyncItems.value = [];
      }
    };

    const resetIncoming = () => {
      getIncoming();
    };

    const onFormEntry = async () => {
      DrawerComponent?.getInstance("view_incoming_details_drawer")?.show();
    };

    const onOpenResponseDrawer = async () => {
      try {
        let res = await ApiService.get(
          `chargebacks/incoming/${incoming.value.id}?action=evidence_images`
        );
        if (res.data.success) {
          if (res.data.data[0] === null) {
            templateImages.value = [];
          } else {
            templateImages.value = res.data.data[0];
          }
        }

        res = await ApiService.get(
          `chargebacks/incoming/${incoming.value.id}?action=response_packet`
        );
        if (res.data.success) {
          if (res.data.data[0] === null) {
            templates.value = {};
          } else {
            templates.value = res.data.data[0];
          }
          DrawerComponent?.getInstance("add_response_drawer")?.show();
        }
      } catch (e) {
        templates.value = {};
        templateImages.value = [];
      }
    };

    const submitTemplate = async () => {
      try {
        let res = await ApiService.get(
          `chargebacks/incoming/${incoming.value.id}?action=evidence_images`
        );
        if (res.data.success) {
          if (res.data.data[0] === null) {
            templateImages.value = [];
          } else {
            templateImages.value = res.data.data[0];
          }
        }

        res = await ApiService.get(
          `chargebacks/incoming/${incoming.value.id}?action=response_packet`
        );
        if (res.data.success) {
          if (res.data.data[0] === null) {
            templates.value = {};
          } else {
            templates.value = res.data.data[0];
          }
        }
      } catch (e) {
        templates.value = {};
        templateImages.value = [];
      }
    };

    const onViewDispute = () => {
      const modal = new Modal(document.getElementById("modal_pdf_view"));
      modal.show();
    };

    return {
      incoming,
      merchant,
      reason,
      reason_explained,
      processor,
      getFoundTime,
      getPopulatedTime,
      resetIncoming,
      getSentTime,
      getSentDate,
      getFoundDate,
      getPopulatedDate,
      connections,

      onOpenResponseDrawer,
      onFormEntry,
      submitTemplate,
      templates,
      templateImages,
      onViewDispute,
      apiSyncItems,
    };
  },
});

